import React from "react";
import { Box, Skeleton } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import { useTooltip } from "@nivo/tooltip";
import { commaNotation } from "@utils";

const niceMaxAbove = (value) => {
  if (value <= 0) return 1;
  const padded = value * 1.3;
  const power = Math.pow(10, Math.floor(Math.log10(padded)));
  const step = power / 2;
  return Math.ceil(padded / step) * step;
};

const formatTick = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(value);
};

const formatPercent = (value, total) => {
  if (!total) return "0%";
  const pct = (value / total) * 100;
  if (pct >= 100) return "100%";
  if (pct < 10) return `${pct.toFixed(1)}%`.replace(/\.0%$/, "%");
  return `${Math.round(pct)}%`;
};

const HyroBarChart = ({ data = [], height = 400, loading = false, showTrack = true, padding = 0.5, total: totalProp }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ height, width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const total = totalProp ?? data[0]?.value ?? 0;
  const yMax = niceMaxAbove(total);
  const chartData = data.map((d) => ({ label: d.label, value: d.value, total }));

  const renderTooltip = (label, value, totalRef) => (
    <BarTooltip>
      <strong>{label}</strong>
      <span>{commaNotation(value)} ({formatPercent(value, totalRef)})</span>
    </BarTooltip>
  );

  const TrackLayer = ({ bars, yScale }) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip();
    const trackTop = yScale(total);
    const trackBottom = yScale(0);
    const trackHeight = Math.max(0, trackBottom - trackTop);
    return (
      <g>
        {bars.map((bar) => {
          const tooltipNode = renderTooltip(
            bar.data.indexValue,
            bar.data.value,
            bar.data.data?.total ?? total
          );
          return (
            <rect
              key={`track-${bar.data.indexValue}`}
              x={bar.x}
              y={trackTop}
              width={bar.width}
              height={trackHeight}
              rx={4}
              fill={theme.palette.info[200]}
              onMouseEnter={(e) => showTooltipFromEvent(tooltipNode, e)}
              onMouseMove={(e) => showTooltipFromEvent(tooltipNode, e)}
              onMouseLeave={() => hideTooltip()}
            />
          );
        })}
      </g>
    );
  };

  const layers = ["grid", "axes"];
  if (showTrack) layers.push(TrackLayer);
  layers.push("bars", "markers", "legends");

  return (
    <Box sx={{ height, width: "100%" }}>
      <ResponsiveBar
        data={chartData}
        keys={["value"]}
        indexBy="label"
        margin={{ top: 16, right: 0, bottom: 40, left: 40 }}
        padding={padding}
        valueScale={{ type: "linear", min: 0, max: yMax }}
        indexScale={{ type: "band", round: true }}
        colors={[theme.palette.secondary[500]]}
        borderRadius={4}
        enableLabel
        labelSkipHeight={20}
        labelTextColor={theme.palette.system[0]}
        labelPosition="end"
        labelOffset={-22}
        label={(d) => `${commaNotation(d.value)} (${formatPercent(d.value, d.data?.data?.total ?? total)})`}
        axisTop={null}
        axisRight={null}
        axisBottom={{ tickSize: 0, tickPadding: 16 }}
        axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5, format: formatTick }}
        enableGridY
        enableGridX={false}
        layers={layers}
        tooltip={({ indexValue, value, data: d }) => renderTooltip(indexValue, value, d.total ?? total)}
        theme={{
          axis: {
            ticks: { text: { fill: theme.palette.text[700], fontSize: 10, fontFamily: "inter,sans-serif" } }
          },
          grid: { line: { stroke: theme.palette.system[200], strokeDasharray: "0" } },
          labels: { text: { fontSize: 14, fontFamily: "inter,sans-serif", letterSpacing: "0.1px" } }
        }}
        animate
        motionConfig="gentle"
      />
    </Box>
  );
};

const BarTooltip = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(0.75),
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  ...theme.typography.caption,
  minWidth: 120,
  color: theme.palette.text[800]
}));

export default HyroBarChart;
